/**
 * 2026-10-09 (Manak, decision 2) — "New lead" on a number that already has a
 * LOST lead must not create a second lead. The create / dedup answers carry
 * `existingStage: 'lost'`, `lost: {…}` and `canReopen` (the server's
 * lost-lead-match.ts, same rule as POST /leads/:id/reopen); this panel shows
 * who had it, when and why, and reopens it through the existing reopen route
 * (stage engine — the lead returns to its pre-lost stage, owner unchanged,
 * history in the audit log). Without `canReopen` it says who can.
 */
import { useState } from 'react';

export interface LostLeadInfo {
  leadId: string;
  name: string | null;
  ownerId: string | null;
  ownerName: string | null;
  lostAt: string | null;
  reason: string | null;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// IST calendar date with a fixed month table ("14 Sep 2026") — ICU spells en-GB
// September "Sept" on some engines, so no Intl month names here.
function istDate(iso: string): string {
  const d = new Date(new Date(iso).getTime() + 330 * 60_000);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export function lostLeadSummary(l: LostLeadInfo): string {
  const parts = [l.name || '(no name)', l.ownerName ? `owner ${l.ownerName}` : 'no owner'];
  if (l.lostAt) parts.push(`lost on ${istDate(l.lostAt)}`);
  if (l.reason) parts.push(`reason: ${l.reason}`);
  return parts.join(' · ');
}

/** Pull the lost block out of a 409 body or a dedup answer (null when the match is not lost). */
export function lostFromAnswer(a: any): { lost: LostLeadInfo; canReopen: boolean } | null {
  if (!a || a.existingStage !== 'lost' || !a.lost?.leadId) return null;
  return { lost: a.lost as LostLeadInfo, canReopen: a.canReopen === true };
}

export function LostLeadReopen({ lost, canReopen, onReopened }: {
  lost: LostLeadInfo;
  canReopen: boolean;
  onReopened: (leadId: string, toStage: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function reopen() {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`/api/v1/leads/${lost.leadId}/reopen`, { method: 'POST', credentials: 'include' });
      const j = await res.json().catch(() => null);
      if (!res.ok) throw new Error(j?.error || (j?.reason === 'not_lost' ? 'This lead is no longer lost.' : `Could not reopen (HTTP ${res.status}).`));
      const toStage = (j?.toStage as string | undefined) ?? null;
      setDone(`Reopened${toStage ? ` — it is back in ${toStage}` : ''}.`);
      onReopened(lost.leadId, toStage);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-3 rounded border border-amber-300 bg-amber-50" data-lost-lead>
      <div className="text-sm font-medium text-amber-900">This customer has a lost lead</div>
      <div className="text-sm text-gray-800 mt-1">{lostLeadSummary(lost)}</div>
      <div className="text-xs text-gray-600 mt-1">A new lead is not created. Reopen the lost one to work this customer again.</div>
      {done ? (
        <div className="text-sm text-green-800 mt-2">{done}</div>
      ) : canReopen ? (
        <button
          type="button"
          onClick={reopen}
          disabled={busy}
          className="mt-2 px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
        >
          {busy ? 'Reopening…' : 'Reopen lost lead'}
        </button>
      ) : (
        <div className="text-xs text-gray-700 mt-2">
          Only {lost.ownerName ?? 'the owner'}, their manager or the lead desk can reopen it. Ask them, or request access to the lead.
        </div>
      )}
      {error && <div className="text-sm text-red-700 mt-2">{error}</div>}
    </div>
  );
}
